import { useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { Upload } from "lucide-react";
import { uploadMedia, resolveMediaUrl } from "../../../lib/api";
import { toast } from "sonner";

const emptyForm = {
  type_id: "", size_id: "", pages_id: "", cover_id: "", price: "",
  paper_id: "", laminate_id: "", color_ids: [], personalization_ids: [],
  feature_notes: "", cover_description: "", image_url: "", active: true, archived: false,
};

export const AlbumLineForm = ({ attributes, initial, onSubmit, onCancel }) => {
  const [form, setForm] = useState(() => initial
    ? { ...initial, paper_id: initial.paper_id || "", laminate_id: initial.laminate_id || "", feature_notes: (initial.feature_notes || []).join(", ") }
    : emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleMulti = (field, id) => setForm((f) => ({
    ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id],
  }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadMedia(file, "image");
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Imagen subida");
    } catch {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!form.type_id || !form.size_id || !form.pages_id || !form.cover_id || !form.price) {
      toast.error("Completa tipo, tamaño, páginas, portada y precio");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      paper_id: form.paper_id || null,
      laminate_id: form.laminate_id || null,
      feature_notes: form.feature_notes.split(",").map((s) => s.trim()).filter(Boolean),
    };
    const ok = await onSubmit(payload);
    setSaving(false);
    if (ok) onCancel();
  };

  const opts = (kind) => attributes[kind] || [];

  return (
    <div className="space-y-4" data-testid="album-line-form">
      <div className="grid grid-cols-2 gap-3">
        {["type_id", "size_id", "pages_id", "cover_id"].map((field) => (
          <div key={field}>
            <Label>{{ type_id: "Tipo", size_id: "Tamaño", pages_id: "Páginas", cover_id: "Portada" }[field]}</Label>
            <Select value={form[field]} onValueChange={(v) => setForm((f) => ({ ...f, [field]: v }))}>
              <SelectTrigger data-testid={`album-line-form-${field}`} className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                {opts(field.replace("_id", "")).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div>
        <Label>Precio (MXN)</Label>
        <Input data-testid="album-line-form-price" type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Papel (informativo, opcional)</Label>
          <Select value={form.paper_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, paper_id: v === "none" ? "" : v }))}>
            <SelectTrigger data-testid="album-line-form-paper_id" className="mt-1"><SelectValue placeholder="Ninguno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Ninguno</SelectItem>
              {opts("paper").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Laminado (informativo, opcional)</Label>
          <Select value={form.laminate_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, laminate_id: v === "none" ? "" : v }))}>
            <SelectTrigger data-testid="album-line-form-laminate_id" className="mt-1"><SelectValue placeholder="Ninguno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Ninguno</SelectItem>
              {opts("laminate").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Colores disponibles (deja vacío si esta portada no ofrece color)</Label>
        <div className="flex flex-wrap gap-3 mt-1" data-testid="album-line-form-colors">
          {opts("color").map((c) => (
            <label key={c.id} className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={form.color_ids.includes(c.id)} onCheckedChange={() => toggleMulti("color_ids", c.id)} data-testid={`album-line-form-color-${c.id}`} />
              {c.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>Personalizaciones seleccionables (deja vacío si no aplica, mismo precio siempre)</Label>
        <div className="flex flex-wrap gap-3 mt-1" data-testid="album-line-form-personalizations">
          {opts("personalization").map((p) => (
            <label key={p.id} className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={form.personalization_ids.includes(p.id)} onCheckedChange={() => toggleMulti("personalization_ids", p.id)} data-testid={`album-line-form-personalization-${p.id}`} />
              {p.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>Características informativas (separadas por coma, ej. "Portada personalizada")</Label>
        <Input data-testid="album-line-form-feature-notes" value={form.feature_notes} onChange={(e) => setForm((f) => ({ ...f, feature_notes: e.target.value }))} />
      </div>

      <div>
        <Label>Descripción de la portada (opcional)</Label>
        <Textarea data-testid="album-line-form-description" value={form.cover_description} onChange={(e) => setForm((f) => ({ ...f, cover_description: e.target.value }))} />
      </div>

      <div>
        <Label>Imagen</Label>
        <div className="flex items-center gap-3 mt-1">
          {form.image_url && <img src={resolveMediaUrl(form.image_url)} alt="" className="h-16 w-16 object-cover rounded" />}
          <label className="flex items-center gap-2 border rounded px-3 py-2 text-xs cursor-pointer hover:bg-secondary/50">
            <Upload size={14} /> {uploading ? "Subiendo..." : "Subir imagen"}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} data-testid="album-line-form-image-input" />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="album-line-form-active" /> Activa</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.archived} onCheckedChange={(v) => setForm((f) => ({ ...f, archived: v }))} data-testid="album-line-form-archived" /> Archivada</label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="album-line-form-cancel">Cancelar</Button>
        <Button onClick={submit} disabled={saving} data-testid="album-line-form-submit">{saving ? "Guardando..." : "Guardar"}</Button>
      </div>
    </div>
  );
};
